/**
 * Guided links (docs/TEAM-CONSOLE.md "Guided links"): the places in the CleanCleaver app staff may send a person to. One list, used
 * by the server (the connector builds `?go=<id>&gp=<param>&gm=<message>` links) and by the app (it opens the path after sign-in).
 * Only these ids are accepted, the param is a plain id (never a URL), so a link can't point anywhere else.
 */
export interface GuidedDestination { id: string; label: string; needs?: "guide" | "check" | "ticket"; /** A stricter shape for this destination's param. */ paramShape?: RegExp; path: (param: string | null) => string }

export const GUIDED_DESTINATIONS: readonly GuidedDestination[] = [
  { id: "plan", label: "Their plan and billing", path: () => "/settings#billing" },
  /** The demo's "Use it free for 25 days" (owner, 2026-10-12): after sign-in and their own setup, the card step (Stripe Checkout). */
  { id: "trial", label: "Start the 25 free days (card)", path: () => "/billing/start" },
  { id: "add_person", label: "Add a person", path: () => "/people/add" },
  { id: "guide", label: "A guide (library topic id)", needs: "guide", path: (p) => `/learn/library/${encodeURIComponent(p ?? "")}` },
  { id: "check", label: "How to do a check (template id)", needs: "check", path: (p) => `/learn/how/${encodeURIComponent(p ?? "")}` },
  { id: "setup", label: "Setup steps", path: () => "/start/ready" },
  { id: "binder", label: "The binder (proof for the inspector)", path: () => "/prove" },
  { id: "today", label: "Today", path: () => "/today" },
  { id: "messages", label: "Their messages from us", path: () => "/messages" },
  // One support request's thread in Messages ("Your requests"), highlighted (owner, 2026-10-12: "Reply in CleanCleaver"). A ticket
  // that isn't theirs, or is gone, just opens Messages: the param is a number, never a URL.
  { id: "ticket", label: "Their support request (ticket number)", needs: "ticket", paramShape: /^\d{1,9}$/, path: (p) => `/messages?ticket=${encodeURIComponent(p ?? "")}` },
];

export const GUIDED_PARAM = /^[A-Za-z0-9_-]{1,80}$/;
export const GUIDED_MESSAGE_ID = /^[A-Za-z0-9_-]{1,64}$/;

/** `?go=…&gp=…&gm=…` → where to open, or null for anything not on the list. */
export function guidedFromQuery(search: string): { dest: string; param: string | null; messageId: string | null; path: string } | null {
  const q = new URLSearchParams(search);
  const d = GUIDED_DESTINATIONS.find((x) => x.id === q.get("go"));
  if (!d) return null;
  const gp = q.get("gp"), gm = q.get("gm");
  const param = gp && GUIDED_PARAM.test(gp) && (!d.paramShape || d.paramShape.test(gp)) ? gp : null;
  if (d.needs && !param) return null;
  return { dest: d.id, param, messageId: gm && GUIDED_MESSAGE_ID.test(gm) ? gm : null, path: d.path(param) };
}
